/**
 * Reset Password Form Component
 *
 * A form for setting a new password after clicking a reset link.
 * Users are redirected here from the email reset link.
 *
 * @fileoverview Reset password form with Supabase authentication
 *
 * @example
 * <ResetPasswordForm />
 */

import React, { useState, FormEvent, useCallback, useEffect } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Text,
  Link as ChakraLink,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { validatePassword, validateConfirmPassword } from '../../utils/validation';
import AuthLayout from './AuthLayout';

/**
 * Reset Password Form Component
 *
 * Handles setting a new password after password reset request.
 * Features:
 * - Password strength validation
 * - Confirm password matching
 * - Password visibility toggle
 * - Success redirect to login
 *
 * @returns Reset password form component
 *
 * @example
 * function ResetPasswordPage() {
 *   return <ResetPasswordForm />;
 * }
 */
export const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const { updatePassword, loading, error, clearError, session } = useAuth();

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({
    password: null as string | null,
    confirmPassword: null as string | null,
  });
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false,
  });

  // Success state
  const [success, setSuccess] = useState(false);
  const [noSession, setNoSession] = useState(false);

  // Color mode values
  const linkColor = useColorModeValue('brand.secondary', 'brand.secondaryLight');
  const successColor = useColorModeValue('green.500', 'green.300');
  const errorColor = useColorModeValue('red.500', 'red.300');

  // Password requirements
  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  /**
   * Check if user has a valid session (from email link)
   */
  useEffect(() => {
    // Give Supabase time to process the URL hash and establish session
    const timer = setTimeout(() => {
      if (!session) {
        setNoSession(true);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [session]);

  /**
   * Handle field blur for validation
   */
  const handleBlur = useCallback(
    (field: 'password' | 'confirmPassword') => {
      setTouched((prev) => ({ ...prev, [field]: true }));

      if (field === 'password') {
        setErrors((prev) => ({ ...prev, password: validatePassword(password) }));
      } else {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: validateConfirmPassword(password, confirmPassword),
        }));
      }
    },
    [password, confirmPassword]
  );

  /**
   * Validate form
   */
  const validateForm = useCallback((): boolean => {
    const newErrors = {
      password: validatePassword(password),
      confirmPassword: validateConfirmPassword(password, confirmPassword),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((err) => err !== null);
  }, [password, confirmPassword]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      clearError();

      setTouched({ password: true, confirmPassword: true });

      if (!validateForm()) {
        return;
      }

      const result = await updatePassword(password);

      if (result.success) {
        setSuccess(true);
        // Redirect to home/login after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    },
    [password, updatePassword, validateForm, clearError, navigate]
  );

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  /**
   * Toggle confirm password visibility
   */
  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  // Show error if no valid session
  if (noSession) {
    return (
      <AuthLayout
        title="Invalid or Expired Link"
        subtitle="This password reset link is no longer valid"
      >
        <VStack spacing={6}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              The password reset link has expired or is invalid. Please request
              a new password reset.
            </AlertDescription>
          </Alert>
          <ChakraLink
            as={RouterLink}
            to="/forgot-password"
            color={linkColor}
            fontWeight="medium"
          >
            Request New Reset Link
          </ChakraLink>
        </VStack>
      </AuthLayout>
    );
  }

  // Show success message
  if (success) {
    return (
      <AuthLayout
        title="Password Updated"
        subtitle="Your password has been successfully changed"
      >
        <VStack spacing={6}>
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              Your password has been updated successfully. You will be
              redirected to the login page.
            </AlertDescription>
          </Alert>
          <Text fontSize="sm" color="gray.500">
            Redirecting in 3 seconds...
          </Text>
          <ChakraLink
            as={RouterLink}
            to="/"
            color={linkColor}
            fontWeight="medium"
          >
            Go to Sign In now
          </ChakraLink>
        </VStack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set New Password"
      subtitle="Create a new password for your account"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={5}>
          {/* Error Alert */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* New Password Field */}
          <FormControl isInvalid={touched.password && !!errors.password}>
            <FormLabel>New Password</FormLabel>
            <InputGroup size="lg">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleBlur('password')}
                autoComplete="new-password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={togglePasswordVisibility}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.password}</FormErrorMessage>

            {/* Password Requirements */}
            {password.length > 0 && (
              <List spacing={1} mt={2}>
                {passwordRequirements.map((req, index) => (
                  <ListItem
                    key={index}
                    fontSize="xs"
                    color={req.met ? successColor : errorColor}
                  >
                    <ListIcon
                      as={req.met ? CheckCircleIcon : WarningIcon}
                      color={req.met ? successColor : errorColor}
                    />
                    {req.text}
                  </ListItem>
                ))}
              </List>
            )}
          </FormControl>

          {/* Confirm Password Field */}
          <FormControl
            isInvalid={touched.confirmPassword && !!errors.confirmPassword}
          >
            <FormLabel>Confirm New Password</FormLabel>
            <InputGroup size="lg">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                autoComplete="new-password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={
                    showConfirmPassword ? 'Hide password' : 'Show password'
                  }
                  icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={toggleConfirmPasswordVisibility}
                  variant="ghost"
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
          </FormControl>

          {/* Submit Button */}
          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            w="full"
            isLoading={loading}
            loadingText="Updating..."
          >
            Update Password
          </Button>
        </VStack>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordForm;
